import {inject, Injectable, signal} from "@angular/core"
import {ConfiguratorVariant} from "@cm/pricing"
import {colorToString} from "@cm/utils"
import {fetchIgnoringErrors} from "@common/helpers/api/fetch"
import {AsyncCacheMap} from "@common/helpers/async-cache-map/async-cache-map"
import {Dictionary} from "@legacy/helpers/utils"
import {BehaviorSubject, from, Observable, of, Subject} from "rxjs"
import {
    BooleanInfo,
    ConfigInfo,
    InterfaceDescriptor,
    InterfaceDescriptor as InterfaceDescriptorNew,
    isConfigInput,
    MaterialInfo,
    NumberInfo,
    StringInfo,
    UiStyle,
    VariantInfo,
} from "@cm/template-nodes"
import {DataObjectThumbnailForConfigMenuServiceGQL} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.generated"

export type MaterialSelectionEvent = {input: MaterialInfo; legacyId: number}
export type ConfigSelectionEvent = {config: ConfigInfo; variant: VariantInfo}
export type ValueChangedEvent = {config: BooleanInfo | NumberInfo | StringInfo; value: boolean | number | string | undefined}

@Injectable({
    providedIn: "root",
})
export class ConfigMenuService {
    configVariantColors: Dictionary<string> = {}
    private readonly dataObjectThumbnailForConfigMenuService = inject(DataObjectThumbnailForConfigMenuServiceGQL)

    private dataObjectThumbnailCache = new AsyncCacheMap<number, string | null>((dataObjectLegacyId) =>
        from(
            (async () => {
                const result = await fetchIgnoringErrors(this.dataObjectThumbnailForConfigMenuService)({legacyId: dataObjectLegacyId})
                return result?.configuratorDataObjectThumbnail ?? null
            })(),
        ),
    )

    /*Remark: The propagation chain of of interface descriptors is:
    SceneManagerService.$descriptorList (signal) -> SceneManagerService.descriptorList$ (observable) -> ConfigMenuService.setInterface

    by subscribing to descriptorList$ in the configurator.component.ts
    There is a synchronization problem related to Configurator.syncConfigChangesAndNotify: This function sends an event configurationLoaded,
    that is evaluated before the above observable chain. It is also evaluated before any effects on SceneManagerService.$descriptorList.
    
    This means that after configuration loaded, the interface available in the config menu service was not updated. For our menus this is not 
    a problem, they act on observables of ConfigMenuService. However, the latest descriptor list is best fetched directly from the SceneManagerService.

    Note that emitting the configurationLoaded in the same way as the signals are handled (triggering it by a signal/effect) solves this.*/
    private interfaceSubject = new BehaviorSubject<InterfaceDescriptorNew<unknown, object>[]>([])
    interfaceNew$ = this.interfaceSubject.asObservable()
    setInterface(interfaceDescriptors: InterfaceDescriptorNew<unknown, object>[]): void {
        this.interfaceSubject.next(interfaceDescriptors)
        this.updateConfigVariantColors()
    }

    private uiStyleSubject = new BehaviorSubject<UiStyle | undefined>(undefined)
    uiStyle$ = this.uiStyleSubject.asObservable()
    setUiStyle(uiStyle: UiStyle): void {
        this.uiStyleSubject.next(uiStyle)
    }

    private iconSizeSubject = new BehaviorSubject<number>(24)
    iconSize$ = this.iconSizeSubject.asObservable()
    setIconSize(iconSize: number): void {
        this.iconSizeSubject.next(iconSize)
    }

    private useSectionedMenuSubject = new BehaviorSubject<boolean>(true)
    useSectionedMenu$ = this.useSectionedMenuSubject.asObservable()
    setUseSectionedMenu(useSectionedMenu: boolean): void {
        this.useSectionedMenuSubject.next(useSectionedMenu)
    }

    readonly $selectedDescriptorIds = signal<Set<string>>(new Set())

    private resetMenuSubject = new Subject<void>()
    resetMenu$ = this.resetMenuSubject.asObservable()
    resetMenu(): void {
        this.$selectedDescriptorIds.set(new Set())
        this.resetMenuSubject.next()
    }

    private synchronizingSubject = new BehaviorSubject<boolean>(false)
    synchronizing$ = this.synchronizingSubject.asObservable()
    setSynchronizing(synchronizing: boolean): void {
        this.synchronizingSubject.next(synchronizing)
    }

    //Outputs of the menu
    private materialSelectedSubject = new Subject<MaterialSelectionEvent>()
    materialSelected$ = this.materialSelectedSubject.asObservable()
    emitMaterialSelected(materialEvent: MaterialSelectionEvent): void {
        this.materialSelectedSubject.next(materialEvent)
    }

    private configSelectedSubject = new Subject<ConfigSelectionEvent>()
    configSelected$ = this.configSelectedSubject.asObservable()
    emitConfigSelected(configEvent: ConfigSelectionEvent): void {
        this.configSelectedSubject.next(configEvent)
    }

    private valueChangedSubject = new Subject<ValueChangedEvent>()
    valueChanged$ = this.valueChangedSubject.asObservable()
    emitValueChanged(valueEvent: ValueChangedEvent): void {
        this.valueChangedSubject.next(valueEvent)
    }

    getSelectedVariantForGroup(groupId: string): ConfiguratorVariant {
        const desc = this.interfaceSubject.getValue().find((desc) => desc.props.id === groupId)
        if (!desc) throw new Error(`Group ${groupId} not found`)
        if (!isConfigInput(desc)) throw new Error(`Group ${groupId} is not a config input`)

        const value = desc.props.value
        if (!value) throw new Error(`Group ${groupId} has no value selected`)

        const variant = desc.props.variants.find((v) => v.id === value.id)
        if (!variant) throw new Error(`Variant with id ${value.id} not found in group ${groupId}`)

        return {
            groupId: desc.props.id,
            groupName: desc.props.name,
            variantId: value.id,
            variantName: variant.name,
        }
    }

    getVariant(variantId: string): VariantInfo | undefined {
        for (const descriptor of this.interfaceSubject.getValue()) {
            if (!isConfigInput(descriptor)) continue
            for (const variant of descriptor.props.variants) {
                if (variant.id === variantId) return variant
            }
        }

        return undefined
    }

    getDescriptorForVariant(variantId: string): InterfaceDescriptor<unknown, object> | undefined {
        for (const descriptor of this.interfaceSubject.getValue()) {
            if (!isConfigInput(descriptor)) continue
            for (const variant of descriptor.props.variants) {
                if (variant.id === variantId) return descriptor
            }
        }

        return undefined
    }

    fetchThumbnailForDataObject(id?: number): Observable<string | null> {
        if (id != null) {
            return this.dataObjectThumbnailCache.get(id)
        } else {
            return of(null)
        }
    }

    private thumbnailLightnessSubjects = new Map<number, BehaviorSubject<boolean | null>>()
    private thumbnailLightnessSampling = new Set<number>()

    isThumbnailLight$(id: number): Observable<boolean | null> {
        return this.getOrCreateThumbnailLightnessSubject(id).asObservable()
    }

    sampleThumbnailLightness(id: number, img: HTMLImageElement): void {
        const subject = this.getOrCreateThumbnailLightnessSubject(id)
        if (subject.value !== null) return
        if (this.thumbnailLightnessSampling.has(id)) return
        this.thumbnailLightnessSampling.add(id)
        sampleImageElementLightness(img).then((isLight) => {
            this.thumbnailLightnessSampling.delete(id)
            subject.next(isLight)
        })
    }

    private getOrCreateThumbnailLightnessSubject(id: number): BehaviorSubject<boolean | null> {
        let subject = this.thumbnailLightnessSubjects.get(id)
        if (!subject) {
            subject = new BehaviorSubject<boolean | null>(null)
            this.thumbnailLightnessSubjects.set(id, subject)
        }
        return subject
    }

    updateConfigVariantColors(): void {
        for (const config of this.interfaceSubject.getValue()) {
            if (!isConfigInput(config)) continue
            for (const variant of config.props.variants) {
                let colorString = "#000000"
                if (variant.iconColor) colorString = colorToString(variant.iconColor as [number, number, number])
                this.configVariantColors[variant.id] = colorString
            }
        }
    }
}

async function sampleImageElementLightness(img: HTMLImageElement): Promise<boolean> {
    const size = 32
    try {
        const bitmap = await createImageBitmap(img, {
            resizeWidth: size,
            resizeHeight: size,
            resizeQuality: "low",
        })
        try {
            const canvas = document.createElement("canvas")
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext("2d")
            if (!ctx) return false
            ctx.drawImage(bitmap, 0, 0)
            const data = ctx.getImageData(0, 0, size, size).data
            let total = 0
            let count = 0
            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] === 0) continue
                const r = data[i] / 255
                const g = data[i + 1] / 255
                const b = data[i + 2] / 255
                total += 0.299 * r + 0.587 * g + 0.114 * b
                count++
            }
            if (count === 0) return false
            return total / count > 0.6
        } finally {
            bitmap.close()
        }
    } catch {
        return false
    }
}

// //Convenience method relevant for mapping prices programmatically, can be removed later.
// function createNode(configInfo: ConfigInfo): string {
//     const varName = configInfo.props.name.replace(/\s+/g, "")
//     const groupId = unwrapInterfaceId(configInfo.props.id)[1]
//     const groupName = configInfo.props.name
//     const groupIdLit = JSON.stringify(groupId)
//     const groupNameLit = JSON.stringify(groupName)

//     const variants = configInfo.props.variants
//     const selectedId = configInfo.props.value?.id
//     const currentVariant = (selectedId !== undefined ? variants.find((v) => v.id === selectedId) : undefined) ?? variants[0]

//     const variantLit = (variantId: string, variantName: string) =>
//         `{groupId: ${groupIdLit}, groupName: ${groupNameLit}, variantId: ${JSON.stringify(variantId)}, variantName: ${JSON.stringify(variantName)}}`

//     const currentVariantLit = variantLit(currentVariant.id, currentVariant.name)
//     const variantComments = variants.map((v) => `//${v.id}, ${v.name}`).join("\n")

//     return `let ${varName} = new ConfigurationGroupNode({parameters: {groupId: ${groupIdLit}, groupName: ${groupNameLit}, currentVariant: ${currentVariantLit}, allVariants: []}})\n${variantComments}`
// }

//the following goes into the service
//public interfaceCache: Map<string, Array<ConfigInfo>> = new Map()

// this.cacheInterface(interfaceDescriptors)
// this.printNodes()

// cacheInterface(node: InterfaceDescriptor<unknown, object>[]): void {
//     node.forEach((desc) => {
//         if (isConfigInput(desc)) {
//             const newVariantIds = desc.props.variants.map((v) => v.id)

//             const key = unwrapInterfaceId(desc.props.id)[1]
//             if (this.interfaceCache.has(key)) {
//                 const existingDescriptors = this.interfaceCache.get(key)!

//                 const exists = existingDescriptors.some((existingDesc) => {
//                     const existingVariantIds = existingDesc.props.variants.map((v) => v.id)
//                     return existingVariantIds.length === newVariantIds.length && newVariantIds.every((id) => existingVariantIds.includes(id))
//                 })

//                 if (!exists) existingDescriptors.push(desc)
//             } else {
//                 this.interfaceCache.set(key, [desc])
//             }
//         }
//     })
// }

// printNodes() {
//     let result = ""

//     this.interfaceCache.forEach((values, _key) => {
//         const prefix = values.length !== 1 ? "!" : ""

//         values.forEach((value) => {
//             result += prefix + createNode(value) + "\n\n"
//         })
//     })

//     console.log(result)

//     // const nodesAsArray = Array.from(this.interfaceCache.values()).flat()
//     // console.log(JSON.stringify(nodesAsArray))
// }
